import type { RimsType, TireUsage } from "@skladiscenje/contracts";

import { rimsType, tireUsage } from "../api/types.js";
import { AutocompleteInput } from "./AutocompleteInput.js";

export interface TireSetFormValue {
  dimension: string;
  quantity: string; // "" or "1".."9" — kept as a string to drive a <select>.
  maker: string;
  usage: TireUsage | "";
  rimsType: RimsType | "";
}

export function emptyTireSet(): TireSetFormValue {
  return { dimension: "", quantity: "", maker: "", usage: "", rimsType: "" };
}

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/** Content values from AddItemWindow.xaml's rim radio buttons (`alu1RadioButton` "Alu", `fe1RadioButton` "Fe", `brez1RadioButton` "Brez"). */
const RIMS_LABELS: Record<RimsType, string> = { ALU: "Alu", FE: "Fe", BREZ: "Brez" };

/**
 * One tire set's fields — dimension, maker, quantity, wear, rim type —
 * shared between set 1 and set 2 (AddItemWindow.xaml:33-44,74-95,104-105).
 * Wear and rim type stay the closed radio-button option sets legacy used,
 * not free text, and quantity stays the closed 1-9 dropdown
 * (`count1ComboBox`/`count2ComboBox`, populated `for(i=1;i<10;i++)` —
 * AddItemWindow.xaml.cs:197-201).
 */
/** Which of a set's required fields are currently flagged (set on a save attempt). */
export interface TireSetErrors {
  maker?: boolean;
  dimension?: boolean;
  quantity?: boolean;
}

export function TireSetFields({
  ordinal,
  value,
  onChange,
  disabled = false,
  idPrefix,
  invalid,
}: {
  ordinal: 1 | 2;
  value: TireSetFormValue;
  onChange: (next: TireSetFormValue) => void;
  disabled?: boolean;
  idPrefix: string;
  invalid?: TireSetErrors | undefined;
}) {
  return (
    <fieldset className="tire-set-fields" disabled={disabled}>
      <legend>{ordinal === 1 ? "Prva garnitura" : "Druga garnitura"}</legend>

      <AutocompleteInput
        id={`${idPrefix}-maker`}
        label="Znamka gum"
        field="tireMaker"
        uppercase
        value={value.maker}
        onChange={(v) => onChange({ ...value, maker: v })}
        required={ordinal === 1 && !disabled}
        invalid={invalid?.maker}
      />
      <AutocompleteInput
        id={`${idPrefix}-dimension`}
        label="Dimenzija"
        field="dimension"
        uppercase
        value={value.dimension}
        onChange={(v) => onChange({ ...value, dimension: v })}
        required={ordinal === 1 && !disabled}
        invalid={invalid?.dimension}
      />

      <div className="form-field">
        <label htmlFor={`${idPrefix}-quantity`}>Kom.</label>
        <select
          id={`${idPrefix}-quantity`}
          value={value.quantity}
          onChange={(e) => onChange({ ...value, quantity: e.target.value })}
          required={ordinal === 1 && !disabled}
          aria-invalid={invalid?.quantity || undefined}
        >
          <option value="">–</option>
          {QUANTITY_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="radio-group">
        <legend>Obraba</legend>
        <div className="radio-group-options">
          {tireUsage.options.map((u) => (
            <label key={u}>
              <input
                type="radio"
                name={`${idPrefix}-usage`}
                checked={value.usage === u}
                onChange={() => onChange({ ...value, usage: u })}
              />
              {u}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="radio-group">
        <legend>Platišča</legend>
        <div className="radio-group-options">
          {rimsType.options.map((r) => (
            <label key={r}>
              <input
                type="radio"
                name={`${idPrefix}-rims`}
                checked={value.rimsType === r}
                onChange={() => onChange({ ...value, rimsType: r })}
              />
              {RIMS_LABELS[r]}
            </label>
          ))}
        </div>
      </fieldset>
    </fieldset>
  );
}
